import type { PerilRecordDetail } from '../api/types'

/**
 * The Map tab, replicating what the legacy K2 form does.
 *
 * The legacy form uses the **free, keyless Google Maps embed**, and so does
 * this. Google resolves the address query itself, so there is no geocoding call
 * of our own and no latitude or longitude needed, which matters because this
 * application does not read the coordinates PDP holds.
 *
 * **There is no API key, and there must not be one.** No Maps SDK, no Embed API
 * key, no billing account. That is why SCI has never had a Google Maps bill, and
 * keeping it that way is a deliberate decision recorded in CLAUDE.md. Any keyed
 * or billed maps service is out of scope.
 *
 * Note on the fallback link below: `api=1` is Google's URL scheme version
 * marker, not a credential. Do not mistake it for a key.
 */

/** Satellite view, close enough to read the roof line, as the legacy form shows. */
const ZOOM = 17

/**
 * The address to drop a pin on.
 *
 * Built from the structured fields rather than perilAddress, which carries the
 * unit and the whole street number range ("UNIT 3, 8-18 BAY STREET, ..."). A
 * range resolves poorly, and a unit does not move the pin, so the range start is
 * the street address a map should show.
 */
export function mapQueryFor(detail: PerilRecordDetail): string {
  const { address } = detail
  if (!address.streetName) return detail.perilAddress ?? ''

  const street = [address.streetNumberFrom, address.streetName, address.streetType]
    .filter(Boolean)
    .join(' ')
  const locality = [address.suburb, address.state, address.postcode].filter(Boolean).join(' ')
  return [street, locality].filter(Boolean).join(', ')
}

/** The keyless embed the legacy form uses. `t=k` is satellite. */
export function embedUrlFor(query: string): string {
  const params = new URLSearchParams({
    q: query,
    t: 'k',
    z: String(ZOOM),
    output: 'embed',
  })
  return `https://maps.google.com/maps?${params.toString()}`
}

/** Opens Google Maps in a new tab. Also keyless. */
export function externalMapUrlFor(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}
